<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import SectionLayout from './SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DurationInput from 'next/input/DurationInput.vue';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';

const { t } = useI18n();
const duration = ref(0);
const unit = ref(DURATION_UNITS.MINUTES);
const isEnabled = ref(false);
const isSubmitting = ref(false);

const { currentAccount, updateAccount } = useAccount();

watch(
  currentAccount,
  () => {
    const { auto_unassign_enabled, auto_unassign_after } =
      currentAccount.value?.settings || {};

    duration.value = auto_unassign_after;
    isEnabled.value = !!auto_unassign_enabled;

    // Set unit based on duration and its divisibility
    if (duration.value) {
      if (duration.value % (24 * 60) === 0) {
        unit.value = DURATION_UNITS.DAYS;
      } else if (duration.value % 60 === 0) {
        unit.value = DURATION_UNITS.HOURS;
      } else {
        unit.value = DURATION_UNITS.MINUTES;
      }
    }
  },
  { deep: true, immediate: true }
);

const updateAccountSettings = async settings => {
  try {
    isSubmitting.value = true;
    await updateAccount(settings, { silent: true });
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_UNASSIGN.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_UNASSIGN.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const handleSubmit = async () => {
  if (duration.value < 1) {
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_UNASSIGN.DURATION.ERROR'));
    return Promise.resolve();
  }

  return updateAccountSettings({
    auto_unassign_enabled: true,
    auto_unassign_after: duration.value,
  });
};

const handleDisable = async () => {
  duration.value = null;

  return updateAccountSettings({
    auto_unassign_enabled: false,
    auto_unassign_after: null,
  });
};

const toggleAutoUnassign = async () => {
  if (!isEnabled.value) handleDisable();
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.AUTO_UNASSIGN.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.AUTO_UNASSIGN.NOTE')"
    :hide-content="!isEnabled"
    with-border
  >
    <template #headerActions>
      <div class="flex justify-end">
        <Switch v-model="isEnabled" @change="toggleAutoUnassign" />
      </div>
    </template>

    <form class="grid gap-5" @submit.prevent="handleSubmit">
      <WithLabel
        :label="t('GENERAL_SETTINGS.FORM.AUTO_UNASSIGN.DURATION.LABEL')"
        :help-message="t('GENERAL_SETTINGS.FORM.AUTO_UNASSIGN.DURATION.HELP')"
      >
        <div class="gap-2 w-full grid grid-cols-[3fr_1fr]">
          <DurationInput
            v-model="duration"
            v-model:unit="unit"
            min="1"
            max="1438560"
            class="w-full"
          />
        </div>
      </WithLabel>
      <div class="flex gap-2">
        <NextButton
          blue
          type="submit"
          :is-loading="isSubmitting"
          :label="t('GENERAL_SETTINGS.FORM.AUTO_UNASSIGN.UPDATE_BUTTON')"
        />
      </div>
    </form>
  </SectionLayout>
</template>
